import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap.ts';
import { logger } from '@lib/logging-system';
import { createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import type { InterfaceVarReplace } from '@store/reducers/reducerHelpers/thunkHelperShared.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';

export interface InterfaceAddress {
  uuid: string;
  addressUuid: string;
  createdAt: string;
  updatedAt: string;
  isChecked: boolean;
  isValid: boolean;
  addressLine1: string;
  addressLine2: string;
  cityUuid: string;
  city: string;
  neighborhoodUuid: string;
  neighborhood: string;
  localityUuid: string;
  locality: string;
  placeUuid: string;
  place: string;
  districtUuid: string;
  district: string;
  regionUuid: string;
  region: string;
  formatted: string;
  formattedOriginal: string;
  postcode: string;
  countryIsoCode: string;
  countryEmoji: string;
  country: string;
  officialName: string;
  location: {
    lat: number;
    lng: number;
  };
  notes: string[];
}

export interface AddressesReducerState {
  addresses: InterfaceGenericMap<InterfaceAddress>;
}

const initialState: AddressesReducerState = {
  addresses: {},
};

const sliceName = 'addresses';

export const addressesSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      .addCase(getCompanyAddresses.pending, () => {
        logger.info('[addressesReducer]', '🔄 getCompanyAddresses.pending - Fetching addresses...');
      })
      .addCase(getCompanyAddresses.fulfilled, (state, action) => {
        logger.info('[addressesReducer]', '✅ getCompanyAddresses.fulfilled - Raw payload:', action.payload);

        // The API returns { data: { uuid1: {...}, uuid2: {...} } }
        // We need to extract the data property
        const addressData = action.payload.data || action.payload;

        logger.info('[addressesReducer]', '📦 Extracted address data:', addressData);
        logger.info('[addressesReducer]', '📊 Number of addresses:', Object.keys(addressData || {}).length);

        state.addresses = addressData;
      })
      .addCase(getCompanyAddresses.rejected, (_, action) => {
        console.error('❌ getCompanyAddresses.rejected - Error:', action.error);
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        const newAddress = action.payload as InterfaceAddress;
        if (newAddress && newAddress.uuid) {
          state.addresses[newAddress.uuid] = newAddress;
        }
      })
      .addCase(updateAddress.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('addresses', 'uuid'))
      .addCase(deleteAddress.fulfilled, (state, action) => {
        if (action.meta.arg?.urlVarsReplaceMap) {
          const addressUuid = action.meta.arg.urlVarsReplaceMap['{addressUuid}'];
          if (addressUuid && state.addresses[addressUuid]) {
            delete state.addresses[addressUuid];
          }
        }
      });
  },
});

export const getCompanyAddresses = thunkGet.compUrl<InterfaceVarReplace>(sliceName, '/c/{companySlug}/admin/address');
export const updateAddress = thunkPut.compUrl<InterfaceAddress, InterfaceVarReplace>(
  sliceName,
  '/c/{companySlug}/admin/address/{addressUuid}',
);
export const deleteAddress = thunkDelete.compUrl<undefined, InterfaceVarReplace>(
  sliceName,
  '/c/{companySlug}/admin/address/{addressUuid}',
);
export const createAddress = thunkPost.compUrl<Omit<InterfaceAddress, 'uuid'>, InterfaceVarReplace>(
  sliceName,
  '/c/{companySlug}/admin/address',
);

export const selectAddressesState = (state: AppState) => {
  logger.info('[addressesReducer]', '🔍 selectAddressesState - Full addresses state:', state.addresses);
  return state.addresses;
};

export const selectAddresses = (state: AppState) => {
  const addressesState = selectAddressesState(state);
  const addresses = addressesState?.addresses || {};
  logger.info('[addressesReducer]', '🔍 selectAddresses - Returning addresses:', addresses);
  return addresses;
};

export const selectAddress = (addressUuid: string) => (state: AppState) => {
  const addresses = selectAddresses(state);
  return addresses?.[addressUuid];
};

export default addressesSlice.reducer;
